import { CSSProperties, useMemo } from 'react';
import {
  Box,
  Grid,
  makeStyles,
  Paper,
  Slide,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { Button, SelectField, VerticalBarLoader } from '@roc/ui';
import { parseDate } from '@roc/feature-utils';
import { compareDesc } from 'date-fns';
import { Item } from '../../components/item';
import { GalleryPhotoList } from './galleryPhotoList';
import { GalleryPhotoDetail } from './galleryPhotoDetail';
import { GalleryPhotosMap } from './galleryPhotosMap';
import { MoveGalleryPhotosDialog } from './moveGalleryPhotosDialog';
import { GalleryPhoto } from '../types/galleryTypes';

const useStyles = makeStyles(() => ({
  paper: {
    padding: '1rem',
    border: '1px solid lightgray',
    borderRadius: '20px',
  },
  photoUploadsPaper: {
    flexGrow: 1,
    overflow: 'hidden',
    padding: '16px 0px',
  },
  detailPaper: {
    padding: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    minHeight: '600px',
    maxHeight: '60vh',
    top: 0,
    position: 'sticky',
  },
  selectFolder: {
    minWidth: '240px',
    '& .MuiOutlinedInput-input': {
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  },
  mapContainer: {
    height: '600px',
    width: '100%',
  },
}));

interface UserMediaGalleryReportProps {
  style?: CSSProperties;
}

export const UserMediaGalleryReport = observer(
  ({ style }: UserMediaGalleryReportProps) => {
    const classes = useStyles();
    const { inspectionStore } = useStore();
    const { galleryStore } = inspectionStore;
    const {
      galleryPhotosStore,
      selectedUser,
      folders,
      selectedFolderId,
    } = galleryStore;

    const photos = useMemo(() => {
      const time = (photo: GalleryPhoto) => {
        const date = parseDate(photo.captureTimeIso);
        return date ? date.getTime() : -Infinity;
      };
      // Sort newest first; photos without a valid capture time go last.
      return [...galleryPhotosStore.photos].sort((a, b) => time(b) - time(a));
    }, [galleryPhotosStore.photos]);
    const isPhotoSelected = !!galleryPhotosStore.photoSelected;

    const onPhotoClick = (photo: GalleryPhoto) => {
      galleryPhotosStore.selectPhoto(selectedUser?.userId, photo);
    };

    return (
      <>
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          gridGap="1rem"
          padding={'8px 24px'}
          minHeight="400px"
          style={style}
        >
          {/* Title */}
          <Box display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" gridGap="1rem">
              <Typography variant="h4">Media Library</Typography>
            </Box>
            <Typography variant="body2">
              {selectedUser?.name || selectedUser?.userName} (User ID:{' '}
              {selectedUser?.userId})
            </Typography>
          </Box>

          {/* Body */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" gridGap="1rem">
                <Paper className={classes.paper} elevation={0}>
                  <Box display="flex" flexDirection={'column'}>
                    <Typography variant="h5">
                      {`${folders.length} Folder${
                        folders.length === 1 ? '' : 's'
                      }`}
                    </Typography>
                    <SelectField
                      options={folders.map(folder => ({
                        label: folder.name,
                        value: folder.folderId,
                      }))}
                      testId="gallery-folder"
                      value={selectedFolderId || ''}
                      onChange={value => galleryStore.selectFolder(value)}
                      fullWidth
                      className={clsx(classes.selectFolder)}
                      variant="outlined"
                      style={{
                        marginTop: '8px',
                      }}
                    />
                  </Box>
                </Paper>

                <Paper
                  className={clsx(classes.paper, classes.photoUploadsPaper)}
                  elevation={0}
                >
                  <Box display="flex" flexDirection="column" gridGap="1rem">
                    <Box display="flex" alignItems="center">
                      <Box flex={1}>
                        <Typography variant="h5" style={{ paddingLeft: 16 }}>
                          {`${photos.length} Photo${
                            photos.length === 1 ? '' : 's'
                          }`}
                        </Typography>
                      </Box>
                      <Box pr={2}>
                        <Button
                          testId="move-gallery-photos"
                          variant="outlined"
                          color="primary"
                          disabled={photos.length === 0}
                          onClick={() => galleryStore.openMovePhotosDialog()}
                        >
                          Move Photos
                        </Button>
                      </Box>
                    </Box>
                    <Box
                      style={{
                        flexGrow: 1,
                        overflowX: 'hidden',
                        padding: '0 16px',
                      }}
                    >
                      <GalleryPhotoList
                        photos={photos}
                        onPhotoClick={onPhotoClick}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                className={clsx(classes.paper, classes.detailPaper)}
                elevation={0}
              >
                {isPhotoSelected ? (
                  <Slide
                    direction="left"
                    in={isPhotoSelected}
                    mountOnEnter
                    unmountOnExit
                  >
                    <Box style={{ width: '100%', padding: 16 }}>
                      <GalleryPhotoDetail />
                    </Box>
                  </Slide>
                ) : (
                  <Box className={classes.mapContainer}>
                    <GalleryPhotosMap
                      photos={photos}
                      onPhotoClick={onPhotoClick}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
        <MoveGalleryPhotosDialog photos={photos} />
        {(galleryStore.isLoading || galleryPhotosStore.isLoading) && (
          <VerticalBarLoader />
        )}
      </>
    );
  }
);
